import React from 'react';
import { Translate } from 'react-localize-redux';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

/**
 * An audit trail rendered for a clinician: who changed what, when and why.
 * Forward-ported from the Hospital line's SHER-636 panel (SKIN-602).
 *
 * Purely presentational and source-agnostic: the caller turns its history rows
 * (a submission's edit history, a patient's personal-data history) into entries
 * whose values are already readable, decrypting client-side where it can. A
 * value it could not decrypt arrives as `encrypted`, never as an empty string,
 * so "this was blank" and "this cannot be read here" stay distinguishable.
 * A recorded reason is immutable: the panel never offers to edit one.
 */
export type AmendmentValue =
    | { kind: 'text'; text: string }
    | { kind: 'empty' }
    | { kind: 'encrypted' };

/** Where a photo went, for a photo moved or attached between lesions (SKIN-576). */
export type PhotoPlacement = 'MOVED_OUT' | 'MOVED_IN' | 'ATTACHED';

interface ChangeTarget {
    label: string;
    /** The repeat the field belongs to, e.g. "C-Lesion #2". */
    context?: string;
}

export type AmendmentChange =
    | ChangeTarget & { kind: 'value'; oldValue: AmendmentValue; newValue: AmendmentValue }
    | ChangeTarget & { kind: 'photo'; placement: PhotoPlacement };

/** EDIT changes values; DELETE removes the record; CREATED is its first recording. */
export type AmendmentAction = 'EDIT' | 'DELETE' | 'CREATED';

export interface AmendmentEntry {
    id: string | number;
    action: AmendmentAction;
    actorName: string;
    at: string; // ISO timestamp
    reason: string | null;
    changes: AmendmentChange[];
}

export interface AmendmentHistoryProps {
    entries: AmendmentEntry[];
    loading?: boolean;
    error?: boolean;
    /** Translation id of the panel title. */
    titleId?: string;
}

const T = 'hospital.patient.amendment-history';

const PHOTO_TEXT: Record<PhotoPlacement, string> = {
    MOVED_OUT: `${T}.photo-moved-out`,
    MOVED_IN: `${T}.photo-moved-in`,
    ATTACHED: `${T}.photo-attached`,
};

const ACTION_CHIP: Partial<Record<AmendmentAction, string>> = {
    DELETE: `${T}.action-deleted`,
    CREATED: `${T}.action-created`,
};

function formatDate(iso: string): string {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleString();
}

const Value: React.FC<{ value: AmendmentValue }> = ({ value }) => {
    if (value.kind === 'text') return <>{value.text}</>;
    if (value.kind === 'encrypted') {
        return <Box component="em"><Translate id={`${T}.encrypted-value`} /></Box>;
    }
    return <>—</>;
};

const ChangeLine: React.FC<{ change: AmendmentChange }> = ({ change }) => (
    <Typography component="li" variant="caption" color="text.secondary">
        {change.label}{change.context ? ` (${change.context})` : ''}:{' '}
        {change.kind === 'value'
            ? <><Value value={change.oldValue} /> → <Value value={change.newValue} /></>
            : <Translate id={PHOTO_TEXT[change.placement]} />}
    </Typography>
);

const Entry: React.FC<{ entry: AmendmentEntry }> = ({ entry }) => {
    const chip = ACTION_CHIP[entry.action];
    return (
        <Box sx={{ py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{entry.actorName}</Typography>
                <Typography variant="caption" color="text.secondary">{formatDate(entry.at)}</Typography>
                {chip && <Chip size="small" variant="outlined" label={<Translate id={chip} />} />}
            </Box>
            {entry.reason && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <Translate id={`${T}.reason`} />: {entry.reason}
                </Typography>
            )}
            {entry.changes.length > 0 && (
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                    {entry.changes.map((change, i) => <ChangeLine key={i} change={change} />)}
                </Box>
            )}
        </Box>
    );
};

export const AmendmentHistory: React.FC<AmendmentHistoryProps> = ({ entries, loading, error, titleId = `${T}.title` }) => {
    let body: React.ReactNode;
    if (loading) {
        body = (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} aria-label="loading" />
            </Box>
        );
    } else if (error) {
        body = <Alert severity="error"><Translate id={`${T}.error`} /></Alert>;
    } else if (entries.length === 0) {
        body = (
            <Typography variant="body2" color="text.secondary">
                <Translate id={`${T}.empty`} />
            </Typography>
        );
    } else {
        body = entries.map((entry, i) => (
            <React.Fragment key={entry.id}>
                {i > 0 && <Divider />}
                <Entry entry={entry} />
            </React.Fragment>
        ));
    }

    return (
        <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                <Translate id={titleId} />
            </Typography>
            {body}
        </Box>
    );
};

export default AmendmentHistory;
